// =============================
// Imports
// =============================

import { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroller';
import _get from 'lodash/get';

import withResponsive from '../../hoc/withResponsive';

import { Desktop } from '../../other/responsive';
import EntityActions from '../../other/entityActions';
import MusicItem from '../../containers/musicitem';
import PageWrapper from '../../other/pagewrapper';

import * as playerCtx from '../../../store/constants/PlayerContexts';

import {
  presentShortDescription,
  isDescriptionTooLong,
  presentDurationEntity,
  presentTags,
} from '../../../helpers/entity/common';
import {
  getAlbumCoverUrl,
  getAlbumTitle,
  getAlbumDisplayArtists,
} from '../../../helpers/entity/album';
import { getBaseRoute, getUrl } from '../../../helpers/entity';
import * as pth from '../../../helpers/proptypes';
import { getLinkRouteProps } from '../../../helpers/link';

import {
  Header,
  Name,
  Title,
  SubName,
  StyledMusicItemOptionsToggler,
  EntityCover,
  EntityDesc,
  Informations,
  DescSeeMore,
  StyledLink,
  SubNameTitle,
  TagsList,
  StyledTag,
  Duration,
  Breadcrumb,
} from '../entity.styles';

// =============================
// Component
// =============================

class Album extends Component {
  static propTypes = {
    album: pth.album.isRequired,
    getTracks: PropTypes.func.isRequired,
    hasMoreTracks: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    isLogged: PropTypes.bool.isRequired,
    locale: PropTypes.string.isRequired,
    openAlbumDescModal: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    tags: PropTypes.arrayOf(pth.tagWithCat).isRequired,
    tracks: PropTypes.arrayOf(pth.track).isRequired,
    parentPage: pth.baseConfigMenu,
  };

  static defaultProps = {
    parentPage: [],
  };

  render() {
    const {
      album,
      getTracks,
      hasMoreTracks,
      isMobile,
      isLogged,
      locale,
      openAlbumDescModal,
      t,
      tags,
      tracks,
      parentPage,
    } = this.props;

    const albumDisplayArtists = getAlbumDisplayArtists(album);

    const displayTags = presentTags(
      _get(album, 'tags', [])
        .map(({ id }) => tags.find(tag => tag.id === id)),
      locale,
    );

    return (
      <InfiniteScroll
        hasMore={hasMoreTracks}
        initialLoad={!isMobile}
        loadMore={getTracks}
        threshold={250}
        useWindow={false}
      >
        <PageWrapper type="entity">
          {parentPage[0] && (
            <Breadcrumb {...getLinkRouteProps(parentPage[0], isLogged)}>
              {t('pages:entity.all_albums')}
            </Breadcrumb>
          )}
          <Header>
            <EntityCover
              placeholderType="album"
              type="album"
              src={getAlbumCoverUrl(album, 'large')}
              lazyload={false}
              alt={getAlbumTitle(album)}
            />
            <Informations>
              <Name>{getAlbumTitle(album)}</Name>

              {albumDisplayArtists.length > 0 && (
                <SubName>
                  {t('pages:entity.album_by')}
                  &nbsp;
                  <SubNameTitle>
                    {albumDisplayArtists.map((da, i) => (
                      /* eslint-disable-next-line react/no-array-index-key */
                      <Fragment key={`da-${da.id}-${i}`}>
                        <StyledLink
                          inline
                          route={getBaseRoute('artists')}
                          nextAs={getUrl('artists')(da, da.name)}
                        >
                          {da.name}
                        </StyledLink>
                        {i < albumDisplayArtists.length - 1 && ', '}
                      </Fragment>
                    ))}
                  </SubNameTitle>
                </SubName>
              )}

              <SubName>
                {_get(album, 'catalog') && (
                  <Fragment>
                    {`${t('pages:entity.catalog')}:`}
                    &nbsp;
                    <SubNameTitle>
                      <StyledLink
                        inline
                        route={getBaseRoute('catalogs')}
                        nextAs={getUrl('catalogs')(album.catalog)}
                      >
                        {_get(album, 'catalog.name')}
                      </StyledLink>
                    </SubNameTitle>
                    {!_get(album, 'albumRef') && _get(album, 'releaseDate') && (
                      <Fragment>&nbsp;-&nbsp;</Fragment>
                    )}
                  </Fragment>
                )}
                {_get(album, 'albumRef') && (
                  <Fragment>
                    {_get(album, 'catalog') && <Fragment>&nbsp;-&nbsp;</Fragment>}
                    {t('pages:entity.ref')}
                    &nbsp;
                    {_get(album, 'albumRef')}
                    {_get(album, 'releaseDate') && <Fragment>&nbsp;-&nbsp;</Fragment>}
                  </Fragment>
                )}
                {_get(album, 'releaseDate') && new Date(_get(album, 'releaseDate')).getFullYear()}
              </SubName>

              {displayTags.length > 0 && (
                <TagsList>
                  {displayTags
                    .slice(0, 8)
                    .map(tag => (
                      <StyledTag key={tag.id} data={tag} />
                    ))}
                </TagsList>
              )}

              {presentShortDescription(_get(album, 'descriptions', []), locale) && (
                <EntityDesc>
                  {presentShortDescription(_get(album, 'descriptions', []), locale)}
                  {isDescriptionTooLong(_get(album, 'descriptions', []), locale) && (
                    <DescSeeMore onClick={openAlbumDescModal}>
                      {t('pages:entity.see_more')}
                    </DescSeeMore>
                  )}
                </EntityDesc>
              )}

              <EntityActions
                doc={album}
                type="album"
                options={{
                  addToPlaylist: true,
                  downloadArchive: true,
                  share: true,
                  duplicatePlaylist: false,
                  downloadCopyright: true,
                }}
                hasTracks={!!tracks.length}
                playerContext={playerCtx.ALBUM}
              />
            </Informations>
          </Header>

          {tracks.length > 0 && (
            <Fragment>
              <Title>
                <div>
                  {_get(album, 'nbTracks')}
                  &nbsp;
                  {t('pages:entity.track', { count: _get(album, 'nbTracks', 0) })}
                  <Desktop>
                    &nbsp;
                    <Duration inline>
                      {`(${t('pages:entity.duration')}: ${presentDurationEntity(
                        _get(album, 'duration', 0),
                      )})`}
                    </Duration>
                  </Desktop>
                </div>
                <StyledMusicItemOptionsToggler />
              </Title>
              {tracks.map((e, i) => (
                <MusicItem
                  key={e.id}
                  music={e}
                  contextName={playerCtx.ALBUM}
                  contextId={album.id}
                  contextPosition={i + 1}
                />
              ))}
            </Fragment>
          )}
        </PageWrapper>
      </InfiniteScroll>
    );
  }
}

export default withResponsive(Album);
